import { IStockIncompleteFormStockItemsSearchResponseData } from "@/api_framework/api_modals/Inventory";
import { useStockItemsSearchListAPI } from "@/app/hooks/api_hooks/Inventory/StockIncompleteForm/useStockItemsSearchListAPI";
import { SectionSearchDataTypes } from "@/app/hooks/api_hooks/emr/useEMRSectionSearchAPI";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { debounce } from "lodash";
import { useEffect, useState, Fragment } from "react";

export type EMRSectionSearchResultType = {
  item_name: string;
};

const StockItemListSearch: React.FC<{
  searchType: SectionSearchDataTypes;
  flexibleAdd: boolean;
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  onSelectItem?: (item: IStockIncompleteFormStockItemsSearchResponseData) => void;
  clearSelectedItem?: () => void;
}> = ({ searchType, containerClassName, inputContainerClassName, onSelectItem, clearSelectedItem, ...props }) => {
  const { stockItems, execute: searchInSection, clearSearchResults } = useStockItemsSearchListAPI();
  const [selected, setSelected] = useState<EMRSectionSearchResultType | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const debounceSearch = debounce(() => {
    searchInSection(debouncedQuery);
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    }
  }, [debouncedQuery]);

  const reducedSearchResults = [...stockItems, { item_name: "Other" }]?.reduce((final, current) => {
    final.push({
      item_name: current?.item_name,
    });
    return final;
  }, [] as EMRSectionSearchResultType[]);

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={(item) => {
          setSelected(item);
          setQuery(item?.item_name || "");
          if (onSelectItem) {
            console.log(item);
            onSelectItem(item);
          }
        }}
      >
        <div className="w-full relative">
          <div className="relative w-full border border-gray-200 cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  className="p-2"
                  onClick={() => {
                    setQuery("");
                    clearSearchResults();
                    clearSelectedItem();
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Input
                autoComplete="off"
                autoCorrect="off"
                placeholder={props?.placeholder ?? "Start Searching ..."}
                className="w-full border-none py-2 pl-3 pr-10 text-xs leading-5 text-gray-900 focus:ring-0"
                onChange={(event) => setQuery(event.target.value)}
                value={query}
              />
            </div>

            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              <div className="w-full pl-4">
                <span className="text-xs">
                  Search results for <span className="text-blue-600 font-semibold">{query}</span>
                </span>
              </div>
              {reducedSearchResults?.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  <p>No Result Found.</p>
                </div>
              ) : (
                reducedSearchResults?.map((result, idx) => (
                  <Combobox.Option
                    key={`${result}:${idx}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-4 pr-2 text-xs ${
                        active
                          ? props?.itemActiveClassName
                            ? props?.itemActiveClassName
                            : "bg-teal-500 text-white"
                          : "text-gray-900"
                      }`
                    }
                    value={result}
                  >
                    {({ selected, active }) => (
                      <div className="flex justify-between items-center">
                        <span
                          data-tooltip-id={`${searchType}:option`}
                          data-tooltip-content={result?.item_name}
                          className={`block text-xs  ${selected ? "font-medium" : "font-normal"} `}
                        >
                          {result.item_name}
                        </span>
                        {selected ? (
                          <span
                            className={`text-xs absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default StockItemListSearch;
